const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const PAID = ['paid', 'success', 'successful', 'completed', 'confirmed'];
const DEAD = ['failed', 'cancelled', 'canceled', 'expired', 'refunded'];

/**
 * Reading whether an order has been paid for.
 *
 * Shared by every gift-card payment screen. This is the one piece of the flow
 * where being wrong costs money in both directions — miss a payment and the
 * customer is charged with nothing to show, call a pending order paid and we
 * issue a voucher for free — so there is exactly one copy of it.
 */

/**
 * An order carries two independent fields and the callers used to read only one.
 *
 *   status          the order's own lifecycle: pending → confirmed
 *   payment_status  the money: pending → paid
 *
 * The gateway settles `payment_status` first; `status` follows once the voucher
 * is issued. Watching `status` alone meant a customer who had genuinely paid sat
 * on "Payment not confirmed yet" until the issue step caught up — which is the
 * gap between the provider saying yes and our screen admitting it.
 *
 * Both are also checked one level down, because /api/orders/{id} has been seen
 * returning the order both flat and wrapped in `{ order: … }`.
 *
 * @returns {'paid'|'failed'|'pending'}
 */
export function verdictFrom(payload) {
  const order = payload?.order || payload || {};
  const fields = [order.payment_status, order.status, payload?.payment_status, payload?.status]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase());

  // Failure wins over success: a refunded order reports both at once, and
  // treating that as paid would re-show a voucher for money already returned.
  if (fields.some((f) => DEAD.includes(f))) return 'failed';
  if (fields.some((f) => PAID.includes(f))) return 'paid';
  return 'pending';
}

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * The gateway's own view of a payment, keyed by `reference_id`.
 *
 * `/api/payment/status/{reference_id}` is the purpose-built endpoint and it
 * answers before the order row moves — the order only becomes `confirmed` once
 * the voucher is issued, which is a step later. We were not calling it at all:
 * the purchase response has carried `reference_id` (`REF-…`) the whole time and
 * the frontend threw it away, then asked this endpoint for a `GC…` order id,
 * which it answers 404 "Payment record not found" for.
 *
 * @returns {'paid'|'failed'|'pending'|'unknown'}
 */
export async function fetchPaymentVerdict(referenceId) {
  if (!referenceId) return 'unknown';
  try {
    const res = await fetch(`${API}/payment/status/${referenceId}`, {
      headers: authHeaders(),
      credentials: 'include',
    });
    if (!res.ok) return 'unknown';
    return verdictFrom(await res.json());
  } catch {
    return 'unknown';
  }
}

/**
 * Both sources, in the order they settle.
 *
 * The gateway moves first, so it is asked first. A wallet-only purchase has no
 * gateway leg and no reference at all, and lands on the order instead — so
 * neither source can be dropped.
 */
export async function fetchSettlementVerdict({ referenceId, orderId }) {
  const fromGateway = await fetchPaymentVerdict(referenceId);
  if (fromGateway === 'paid' || fromGateway === 'failed') return fromGateway;

  const fromOrder = await fetchOrderVerdict(orderId);
  if (fromOrder !== 'unknown') return fromOrder;

  // Neither answered. 'pending' from the gateway is still real information;
  // only report 'unknown' when nothing was learned from either.
  return fromGateway;
}

/**
 * Whether a failed order took the customer's money and gave it back.
 *
 * Since 2026-08-07 the backend auto-refunds to the NSO wallet when the provider
 * refuses to issue after payment has cleared, and refunds only the failed lines
 * of a partly-issued basket. That makes "failed" two different events with the
 * same name, and the difference is the customer's money:
 *
 *   never charged   — the gateway leg did not complete. Nothing to say.
 *   charged, repaid — money moved and came back as wallet credit. Saying
 *                     "nothing was charged" here is a lie, and it is the kind
 *                     that has the customer hunting a refund that already
 *                     arrived.
 *
 * Wallet, not card, because the gateway's refund API is not wired — that is a
 * manual dashboard job. Credit is instant, which beats the money sitting in
 * limbo, but it does mean the customer has to be told where it went.
 *
 * @returns {{refunded: boolean, amount: number|null}}
 */
export async function fetchRefund(orderId) {
  const none = { refunded: false, amount: null };
  if (!orderId) return none;

  try {
    const res = await fetch(`${API}/orders/${orderId}`, {
      headers: authHeaders(),
      credentials: 'include',
    });
    if (!res.ok) return none;

    const payload = await res.json();
    const order = payload?.order || payload || {};

    const flagged = [order.payment_status, order.status, order.refund_status]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes('refund'));

    const amount = Number(
      order.refunded_amount ?? order.refund_amount ?? order.wallet_refund ?? order.total ?? 0
    );

    if (!flagged && !(order.refunded_amount || order.refund_amount || order.wallet_refund)) {
      return none;
    }
    return { refunded: true, amount: Number.isFinite(amount) && amount > 0 ? amount : null };
  } catch {
    return none;
  }
}

/**
 * @returns {'paid'|'failed'|'pending'|'unknown'} — `unknown` for a request that
 * did not complete, which is deliberately distinct from `pending`: a dropped
 * poll is not evidence about the payment, and callers must not act on it.
 */
export async function fetchOrderVerdict(orderId) {
  if (!orderId) return 'unknown';
  const token = localStorage.getItem('token');

  try {
    const res = await fetch(`${API}/orders/${orderId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!res.ok) return 'unknown';
    return verdictFrom(await res.json());
  } catch {
    return 'unknown';
  }
}
